import * as React from 'react'
import clsx from 'clsx'

import { Container } from '../../_shared/Container'
import { SectionHead, SectionHeadHighlight } from '../../_shared/SectionHead'
import { formatInteger, type UluleStats } from '../../_shared/ululeStats'
import { CATEGORY_BY_ID, categoryHref, type Category } from '../categories'
import { CATEGORIES } from '../content'
import { useStatsTracking } from '../tracking'
import styles from './Categories.module.css'

/** How many rows the ledger shows; the feed carries more than the page wants to list. */
const MAX_ROWS = 12

type Row = { name: string; amountEur: number; widthPercent: number; category?: Category }

/** Sorted by amount, with each bar scaled against the largest so the top row always fills. */
function makeRows(stats: UluleStats | null): Row[] {
  const categories = stats?.categories
  if (categories === undefined) return []

  const ranked = [...categories].sort((a, b) => b.amountRaisedEur - a.amountRaisedEur).slice(0, MAX_ROWS)
  const largest = ranked[0]?.amountRaisedEur ?? 0
  if (largest === 0) return []

  return ranked.map((entry) => {
    const category = entry.id === undefined ? undefined : CATEGORY_BY_ID[entry.id]

    return {
      name: entry.name,
      amountEur: entry.amountRaisedEur,
      widthPercent: Math.round((entry.amountRaisedEur / largest) * 100),
      ...(category === undefined ? {} : { category }),
    }
  })
}

export function Categories({ stats }: { stats: UluleStats | null }): React.ReactElement {
  const tracking = useStatsTracking()
  const rows = makeRows(stats)

  return (
    <section className={styles.categories} id="categories">
      <Container>
        <SectionHead
          size="wide"
          kicker={CATEGORIES.kicker}
          title={
            <>
              {'Une immense '}
              <SectionHeadHighlight>diversité</SectionHeadHighlight>
              {' de projets !'}
            </>
          }
          lede={CATEGORIES.lede}
        />
        {rows.length > 0 && (
          <>
            <div className={styles.led}>
              {rows.map((row, index) => {
                const cells = (
                  <>
                    {/* Bar sits behind the row's text; the cells are lifted above it by z-index. */}
                    <span className={styles.lrow__bar} style={{ width: `${row.widthPercent}%` }} aria-hidden="true" />
                    <span className={styles.lrow__n}>{String(index + 1).padStart(2, '0')}</span>
                    <span className={styles.lrow__name}>{row.name}</span>
                    <span className={styles.lrow__val}>{formatInteger(row.amountEur)} €</span>
                  </>
                )

                // A row the feed named but this page does not know stays inert rather than
                // linking nowhere. Bound to a const so the narrowing survives into the closure.
                const category = row.category
                if (category === undefined) {
                  return (
                    <div key={row.name} className={styles.lrow}>
                      {cells}
                    </div>
                  )
                }

                // No `aria-label`: it would REPLACE the accessible name, so the rank and the amount
                // — the point of the ledger — would stop being announced on the rows that link,
                // while the inert rows still read them. The cells name the link well enough.
                return (
                  <a
                    key={row.name}
                    className={clsx(styles.lrow, styles.lrowLink)}
                    href={categoryHref(category.slug)}
                    onClick={() => tracking.categoryClick(category.slug)}
                  >
                    {cells}
                  </a>
                )
              })}
            </div>
            <p className={styles.led__note}>{CATEGORIES.note}</p>
          </>
        )}
      </Container>
    </section>
  )
}
